import AdminContainer from "../../../components/admin/AdminContainer"
import React, {useEffect, useRef, useState} from "react"
import AdminTable from "../../../components/admin/AdminTable"
import {BACKEND_URL, BRAND_NAME} from "../../../data/config"
import {
  Button,
  IconButton,
  Chip,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Card,
  CardContent,
  Typography
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import {Link} from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogTitle from "@mui/material/DialogTitle"
import {useGlobalContext} from "../../../contexts/global_context"
import {Helmet} from "react-helmet"
import moment from "moment";
import MuiLink from "@mui/material/Link";
import Grid from "@mui/material/Grid";

const DeleteDialog = ({deletingItemUid, setDeletingItemUid, fetchData}) => {
  let {sendAjax} = useGlobalContext()

  const DeleteItem = (uid) => {
    sendAjax({
      url: `${BACKEND_URL}/api/v1/pkg/admin/providers/${uid}/`,
      method: "delete",
      data: {},
    }).then((r) => {
      setDeletingItemUid("")
      fetchData()
    })
  }

  return (
    <Dialog
      open={Boolean(deletingItemUid)}
      onClose={() => {
        setDeletingItemUid("")
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Delete Item?"}</DialogTitle>
      <DialogActions>
        <Button
          onClick={() => {
            setDeletingItemUid("")
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            DeleteItem(deletingItemUid)
          }}
          autoFocus
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const ProviderListAdminPage = () => {
  const {sendAjax, showAlert} = useGlobalContext()
  const [page, setPage] = useState(0)
  const [data, setData] = useState({})
  const [deletingItemUid, setDeletingItemUid] = useState("")
  const [selectedSocials, setSelectedSocials] = useState(null)
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const latestRequestIdRef = useRef(0)
  let items = []
  let numTotal = 0

  const socialChoices = [
    {value: 10, label: 'Instagram'},
    {value: 20, label: 'TikTok'},
  ]

  const serviceTypeChoices = [
    {value: 10, label: 'Likes'},
    {value: 20, label: 'Followers'},
    {value: 50, label: 'Views'},
    {value: 30, label: 'R Comments'},
    {value: 40, label: 'C Comments'},
    {value: 90, label: 'B Comments'},
    {value: 100, label: 'Shares'},
  ]

  const fetchData = ({showLoading = true} = {}) => {
    const requestId = ++latestRequestIdRef.current

    const requestData = {page: page + 1}

    // Add social filter if any socials are selected
    if (selectedSocials !== null) {
      requestData.social__in = selectedSocials
    }

    // Add service type filter if any service types are selected
    if (selectedServiceTypes !== null) {
      requestData.service_type__in = selectedServiceTypes
    }

    if (showLoading) {
      setIsLoading(true)
    }

    sendAjax({
      url: BACKEND_URL + "/api/v1/pkg/admin/providers/",
      method: "get",
      data: requestData,
    }).then((r) => {
      if (requestId === latestRequestIdRef.current) {
        setData(r.data)
      }
    }).catch((error) => {
      console.error('Error fetching data:', error)
    }).finally(() => {
      if (showLoading && requestId === latestRequestIdRef.current) {
        setIsLoading(false)
      }
    })
  }

  const handleClickDelete = (uid) => {
    setDeletingItemUid(uid)
  }

  const handleTestNow = (uid) => {
    sendAjax({
      url: `${BACKEND_URL}/api/v1/pkg/admin/providers/${uid}/test-now/`,
      method: "post",
      data: {},
    }).then((r) => {
      fetchData() // Refresh the data to show updated status
    })
  }

  const handleSocialFilterChange = (socialValue) => {
    setSelectedSocials(socialValue)
    setPage(0) // Reset to first page when filter changes
  }

  const handleServiceTypeFilterChange = (serviceTypeValue) => {
    setSelectedServiceTypes(serviceTypeValue)
    setPage(0) // Reset to first page when filter changes
  }

  useEffect(() => {
    fetchData({showLoading: true})
  }, [page, selectedSocials, selectedServiceTypes])

  // Auto-refresh every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData({showLoading: false})
    }, 3000) // 3 seconds

    return () => clearInterval(interval)
  }, [page, selectedSocials, selectedServiceTypes])

  try {
    for (let [index, item] of data.data.results.entries()) {
      // Format last test status
      let lastTestDisplay = ""
      if (item.last_test_status && item.last_test_date) {
        const statusText = item.last_test_status === 20 ? "successful" : "failed"
        const timeAgo = moment(item.last_test_date).fromNow()

        if (item.test_status === 10) {
          lastTestDisplay = `${statusText} ${timeAgo}`
        } else if (item.test_status === 20) {
          lastTestDisplay = `${statusText} ${timeAgo}`
        } else if (item.test_status === 30) {
          lastTestDisplay = `${statusText} ${timeAgo}`
        }
      }

      items.push({
        uid: item.uid,
        code: <>
          <MuiLink
            href={`/admin/providers/update/${item.uid}`}
            target="_blank"
          >
            {item.service_code}
            <br/>
            {item.provider_type_display}
          </MuiLink>
        </>,
        status: <>
          <Chip
            label={`Service: ${item.service_status_display}`}
            color={
              item.service_status === 10 ? "success" : // Available - green
                item.service_status === 20 ? "error" :   // Unavailable - red
                  item.service_status === 30 ? "default" : // Disabled - gray
                    "default"
            }
            variant="outlined"
            size="small"
            sx={{
              marginBottom: 1,
            }}
          />

          <br/>

          <Chip
            label={item.auto_test_enabled ? "Auto Test Enabled" : "Auto Test Disabled"}
            color={item.auto_test_enabled ? "success" : "default"}
            variant="outlined"
            size="small"
          />
        </>,
        title: item.title,
        service_type: item.service_type_display,
        social: item.social_display,
        test_status: <>
          <Chip
            label={item.test_status_display}
            color={
              item.test_status === 10 ? "info" :    // Ready to Test - blue
                item.test_status === 20 ? "info" :  // Testing - orange
                  item.test_status === 30 ? "info" : // Tested - green
                    "default"
            }
            variant="outlined"
            size="small"
            sx={{
              marginBottom: '5px',
            }}
          />

          <span style={{display: 'block'}}>
            {lastTestDisplay}
          </span>

          <Button
            startIcon={<PlayArrowIcon/>}
            variant="text"
            color="primary"
            size="small"
            onClick={() => {
              handleTestNow(item.uid)
            }}
          >
            Test Now
          </Button>

        </>,
        index: item.index,
        actions: (
          <>
            <IconButton
              component={Link}
              to={`/admin/providers/update/${item.uid}`}
            >
              <EditIcon/>
            </IconButton>

            <IconButton
              onClick={() => {
                handleClickDelete(item.uid)
              }}
            >
              <DeleteIcon/>
            </IconButton>
          </>
        ),
      })
    }
    numTotal = data.data.count
  } catch {
  }

  return (
    <AdminContainer>
      <Helmet>
        <title>Providers - {BRAND_NAME}</title>
      </Helmet>

      <DeleteDialog
        {...{deletingItemUid, setDeletingItemUid, fetchData}}
      />

      {/* quick Filter */}
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          marginBottom: '20px',
        }}
        elevation={2}
      >
        {isLoading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
            }}
          />
        )}

        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={3}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                }}
              >
                Filter by Social Platform
              </Typography>
              <RadioGroup
                row
                value={selectedSocials ?? ""}
                onChange={(event) => handleSocialFilterChange(Number(event.target.value))}
                disabled={isLoading}
              >
                {socialChoices.map((social) => (
                  <FormControlLabel
                    key={social.value}
                    control={
                      <Radio
                        value={social.value}
                        checked={selectedSocials === social.value}
                        color="primary"
                      />
                    }
                    label={social.label}
                    sx={{marginRight: 3}}
                  />
                ))}
              </RadioGroup>
            </Grid>

            <Grid item xs={12} sm={12} md={9}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                }}
              >
                Filter by Service Type
              </Typography>
              <RadioGroup
                row
                value={selectedServiceTypes ?? ""}
                onChange={(event) => handleServiceTypeFilterChange(Number(event.target.value))}
                disabled={isLoading}
              >
                {serviceTypeChoices.map((serviceType) => (
                  <FormControlLabel
                    key={serviceType.value}
                    control={
                      <Radio
                        value={serviceType.value}
                        checked={selectedServiceTypes === serviceType.value}
                        color="primary"
                      />
                    }
                    label={serviceType.label}
                    sx={{marginRight: 3}}
                  />
                ))}
              </RadioGroup>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Button
        component={Link}
        to={"/admin/providers/create"}
        variant="outlined"
        startIcon={<AddIcon/>}
        sx={{marginBottom: "20px"}}
      >
        Add New Provider
      </Button>

      <AdminTable
        items={items}
        numTotal={numTotal}
        page={page}
        setPage={setPage}
      />
    </AdminContainer>
  )
}

export default ProviderListAdminPage
